import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../api';
import useAdminDialog from '../components/AdminDialog';
import { useAdminLanguage } from '../AdminLanguageContext';

const yuan = (cents) => (Number(cents || 0) / 100).toFixed(2);

const STATUS_BADGE = {
  pending: 'badge-orange',
  processing: 'badge-blue',
  paid: 'badge-green',
  completed: 'badge-green',
  cancelled: 'badge-gray',
};

export default function CheckoutRequests() {
  const { copy } = useAdminLanguage();
  const checkoutRequestsCopy = copy.checkoutRequests;
  const STATUS_LABEL = {
    pending: checkoutRequestsCopy.status.pending,
    processing: checkoutRequestsCopy.status.processing,
    paid: checkoutRequestsCopy.status.paid,
    completed: checkoutRequestsCopy.status.completed,
    cancelled: checkoutRequestsCopy.status.cancelled,
  };
  const STATUS_OPTIONS = [
    { value: 'all', label: checkoutRequestsCopy.filters.allStatus },
    { value: 'pending', label: checkoutRequestsCopy.status.pending },
    { value: 'processing', label: checkoutRequestsCopy.status.processing },
    { value: 'paid', label: checkoutRequestsCopy.status.paid },
    { value: 'completed', label: checkoutRequestsCopy.status.completed },
    { value: 'cancelled', label: checkoutRequestsCopy.status.cancelled },
  ];
  const [status, setStatus] = useState('pending');
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { confirmAction, dialog } = useAdminDialog();

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    api.get('/admin/finance/checkout-requests', { params: { status } })
      .then(res => setRequests(res.data.requests || []))
      .catch(err => setError(err.response?.data?.error || checkoutRequestsCopy.errors.loadFailed))
      .finally(() => setLoading(false));
  }, [status, checkoutRequestsCopy.errors.loadFailed]);

  useEffect(() => { load(); }, [load]);

  const totalAmount = useMemo(
    () => requests.reduce((sum, item) => sum + Number(item.amount || 0), 0),
    [requests]
  );

  const updateStatus = async (item, nextStatus) => {
    if (item.status === nextStatus) return;
    if (!await confirmAction({
      title: checkoutRequestsCopy.confirm.title,
      message: checkoutRequestsCopy.confirm.message(item.title, STATUS_LABEL[nextStatus]),
      variant: nextStatus === 'cancelled' ? 'danger' : 'primary',
    })) return;
    await api.post(`/admin/finance/checkout-requests/${item.id}/status`, { status: nextStatus });
    load();
  };

  return (
    <div>
      {dialog}
      <div className="page-title">{checkoutRequestsCopy.title}</div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          {STATUS_OPTIONS.map(option => (
            <button
              key={option.value}
              className={`btn btn-sm ${status === option.value ? 'btn-primary' : 'btn-outline'}`}
              onClick={() => setStatus(option.value)}
            >
              {option.label}
            </button>
          ))}
          <span style={{ marginLeft: 'auto', fontSize: 13, color: '#6b7280' }}>
            {checkoutRequestsCopy.summary(requests.length, yuan(totalAmount))}
          </span>
        </div>
      </div>

      {error && (
        <div className="card" style={{ color: '#b91c1c', marginBottom: 16 }}>
          {error}
        </div>
      )}

      <div className="card">
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>{checkoutRequestsCopy.columns.id}</th>
                <th>{checkoutRequestsCopy.columns.applicant}</th>
                <th>{checkoutRequestsCopy.columns.project}</th>
                <th>{checkoutRequestsCopy.columns.amount}</th>
                <th>{checkoutRequestsCopy.columns.description}</th>
                <th>{checkoutRequestsCopy.columns.status}</th>
                <th>{checkoutRequestsCopy.columns.createdAt}</th>
                <th>{checkoutRequestsCopy.columns.actions}</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} style={{ textAlign: 'center', padding: 32, color: '#9ca3af' }}>
                    {checkoutRequestsCopy.loading}
                  </td>
                </tr>
              ) : requests.length === 0 ? (
                <tr>
                  <td colSpan={8} style={{ textAlign: 'center', padding: 32, color: '#9ca3af' }}>
                    {checkoutRequestsCopy.empty}
                  </td>
                </tr>
              ) : requests.map(item => (
                <tr key={item.id}>
                  <td style={{ color: '#9ca3af' }}>#{item.id}</td>
                  <td>
                    <div style={{ fontWeight: 600 }}>{item.username || checkoutRequestsCopy.emptyValue}</div>
                    <div style={{ fontSize: 11, color: '#9ca3af' }}>{item.email || ''}</div>
                  </td>
                  <td style={{ fontWeight: 600, maxWidth: 220 }}>{item.title}</td>
                  <td style={{ color: '#1dbf73', fontWeight: 700 }}>¥ {yuan(item.amount)}</td>
                  <td style={{ maxWidth: 260, color: '#6b7280' }}>{item.description || checkoutRequestsCopy.emptyValue}</td>
                  <td>
                    <span className={`badge ${STATUS_BADGE[item.status] || 'badge-gray'}`}>
                      {STATUS_LABEL[item.status] || item.status}
                    </span>
                  </td>
                  <td style={{ fontSize: 12, color: '#6b7280' }}>
                    {item.created_at ? String(item.created_at).slice(0, 16) : checkoutRequestsCopy.emptyValue}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {item.status !== 'processing' && (
                        <button className="btn btn-sm btn-outline" onClick={() => updateStatus(item, 'processing')}>
                          {checkoutRequestsCopy.actions.markProcessing}
                        </button>
                      )}
                      {item.status !== 'completed' && (
                        <button className="btn btn-sm btn-primary" onClick={() => updateStatus(item, 'completed')}>
                          {checkoutRequestsCopy.actions.complete}
                        </button>
                      )}
                      {item.status !== 'cancelled' && (
                        <button
                          className="btn btn-sm btn-outline"
                          style={{ color: '#ef4444', borderColor: '#fecaca' }}
                          onClick={() => updateStatus(item, 'cancelled')}
                        >
                          {checkoutRequestsCopy.actions.cancel}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
